import React, { Component } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import One from "../../assets/bookingeventassets/assets/1.svg";
import PaypalSVG from "../../assets/bookingeventassets/assets/PayPal.svg";
import CreditCardSVG from "../../assets/bookingeventassets/assets/credit.svg";
import PathSVG from "../../assets/bookingeventassets/assets/path.svg";
import StripeSVG from "../../assets/icons/stripe.svg";
import FormField from "../../components/FormField/FormField";
import ArrowLeft from "../../assets/icons/arrow-left.svg";
import { FormGroup, Input, Label } from "reactstrap";
import isEmpty from "../../validation/is-empty";
import { notification, BACKEND_API_BASE_URL } from "../../utils/utility";
import { getPromoByCode } from "../../actions/admin/promo-actions";
import { connect } from "react-redux";
import Loader from "../../components/Loader/Loader";
import PaypalPaymentFailed from "./Components/PaypalPaymentFailed";
import "../../assets/scss/paypal-custom.scss";
import "../../assets/scss/stripe-custom.scss";

// No validation needed as Stripe Elements handle their own validation
const noValidation = null;

let initialValues = {};

class BookingStep3 extends Component {
  
  constructor(props) {
    super(props);
    this.paypalContainerRef = React.createRef();
    this.stripeCardElementRef = React.createRef();
    // Refs to prevent infinite loops in componentDidUpdate
    this.isProcessingStripeUpdate = false;
    this.isProcessingPayPalUpdate = false;
  }


  state = {
    addOns: [],
    pricing: [],
    couponCode: "",
    discount: 0,
    isLoading: false,
    selectedPaymentMethod: null, // Will be set dynamically based on available methods
    paypalPaymentCompleted: false,
    paypalPaymentFailed: false,
    paypalInitializationAttempts: 0,
    isInitializingPayPal: false, // Flag to prevent multiple simultaneous initializations
    stripeLoaded: false,
    stripeCardElement: null,
    stripeCardErrors: null,
    isProcessingStripePayment: false,
    isInitializingStripe: false, // Flag to prevent multiple simultaneous Stripe initializations
  };

  /**
   * Get total pricing
   * @returns int
   */
  getTotalPricing = () => {
    const { pricing } = this.props;
    let totalPricing = 0;
    (pricing || []).forEach((eventPricing, index) => {
      totalPricing +=
        Number(eventPricing.item_price) * Number(eventPricing.item_qty);
    });
    return totalPricing;
  };

  /**
   * Get total addons
   * @returns int
   */
  getTotalAddons = () => {
    const { addOns } = this.props;
    let totalAddOns = 0;
    (addOns || []).forEach((addOn, index) => {
      totalAddOns += Number(addOn.addon_price) * Number(addOn.addon_qty);
    });
    return totalAddOns;
  };

  onApplyCouponCode = () => {
    const _this = this;

    const { getPromoByCode } = _this.props;
    const { couponCode } = this.state;
    if (isEmpty(couponCode))
      return notification.error("Please enter Coupon code");

    _this.setState({ isLoading: true, discount: 0 });

    getPromoByCode({
      data: {
        couponCode,
      },
      onSuccess: function (response) {
        _this.setState({ isLoading: false });
        const { promo } = response;
        if (isEmpty(promo)) {
          return notification.error("Coupon code does not exist.");
        }
        const couponValue = promo.coupon_value;
        const totalAmount = _this.getTotalPricing() + _this.getTotalAddons();
        if (promo.coupon_payout_type === "P") {
          _this.setState({
            discount: Math.floor((totalAmount / 100) * couponValue),
          });
        } else {
          _this.setState({
            discount: couponValue,
          });
        }
      },
      onError: (error) => {
        _this.setState({ isLoading: false });
      },
    });
  };

  /**
   * Handle payment method selection
   * @param {string} method - "card" or "paypal"
   */
  onPaymentMethodChange = (method) => {
    const _this = this;
    this.setState({ 
      selectedPaymentMethod: method,
      paypalPaymentCompleted: false, // Reset payment status when switching methods
      paypalPaymentFailed: false, // Reset failed status when switching methods
      isInitializingStripe: false,
      stripeCardElement: null,
    });

    if (method == 'card') {
      setTimeout(() => {
          _this.initializeStripeElements();
      }, 500);
    }
  };

  /**
   * Handle PayPal payment retry
   */
  onRetryPayPalPayment = async () => {
    this.setState({ 
      paypalPaymentFailed: false,
      paypalPaymentCompleted: false 
    });
    // Clear existing buttons first, then re-initialize
    await this.clearPayPalButtons();
    setTimeout(() => {
      this.initializePayPalButtons();
    }, 300);
  };

  /**
   * Load PayPal SDK with dynamic client ID
   */
  loadPayPalSDK = () => {
    const { paymentSettings } = this.props;
    
    // Check if PayPal is enabled and client ID exists
    if (!paymentSettings || 
        paymentSettings.is_paypal_enabled !== "E" || 
        !paymentSettings.paypal_client_id) {
      return;
    }

    const paypalClientId = paymentSettings.paypal_client_id;
    
    if (!window.paypal) {
      // Remove existing PayPal script if any
      const existingScript = document.querySelector('script[src*="paypal.com/sdk"]');
      if (existingScript) {
        existingScript.remove();
      }
      
      const script = document.createElement('script');
      script.src = `https://www.paypal.com/sdk/js?client-id=${paypalClientId}&currency=USD&disable-funding=paylater,credit,venmo,applepay`;
      script.async = true;
      script.onload = () => {
        console.log('PayPal SDK loaded successfully with client ID:', paypalClientId);
        // Wait a bit for SDK to fully initialize before creating buttons
        setTimeout(() => {
          this.initializePayPalButtons();
        }, 300);
      };
      script.onerror = (error) => {
        console.error('PayPal SDK failed to load:', error);
        notification.error("Failed to load PayPal. Please refresh the page.");
      };
      document.head.appendChild(script);
    } else {
      console.log('PayPal SDK already loaded');
      // Wait a bit before initializing to ensure SDK is ready
      setTimeout(() => {
        this.initializePayPalButtons();
      }, 300);
    }
  };

  /**
   * Initialize PayPal buttons
   */
  initializePayPalButtons = async () => {
    // Prevent multiple simultaneous initializations
    if (this.state.isInitializingPayPal) {
      console.log('PayPal initialization already in progress, skipping...');
      return;
    }

    // Only initialize if PayPal is the selected payment method
    if (this.state.selectedPaymentMethod !== "paypal") {
      console.log('PayPal is not the selected payment method, skipping initialization');
      return;
    }

    const { onConfirmTempBooking } = this.props;
    const { couponCode, discount } = this.state;
    
    const totalPricing = this.getTotalPricing();
    const totalAddOns = this.getTotalAddons();
    const totalAmount = totalPricing + totalAddOns - discount;

    const container = this.paypalContainerRef.current;
    if (!container || !window.paypal) {
      console.warn('PayPal container not found or SDK not loaded');
      this.setState({ isInitializingPayPal: false });
      return;
    }

    // Set flag to prevent multiple initializations
    this.setState({ isInitializingPayPal: true });

    try {
      // Clear existing buttons safely and wait for cleanup to complete
      await this.clearPayPalButtons();

      // Additional check to ensure container is still available
      if (!this.paypalContainerRef.current) {
        console.warn('PayPal container removed during cleanup');
        this.setState({ isInitializingPayPal: false });
        return;
      }

      const buttons = window.paypal.Buttons({
        style: {
          layout: 'vertical',
          color: 'blue',
          shape: 'rect',
          label: 'paypal',
          height: 45
        },

        /** 🔑 CREATE ORDER (BACKEND) */
        createOrder: () => {
          // Construct the API URL - try with base URL first, fallback to relative
          const apiUrl = `${BACKEND_API_BASE_URL}paypal/create-order`;
          
          return fetch(apiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              amount: totalAmount,
              booking_id: this.props.bookingId
            })
          })
          .then(async (res) => {
            // Check if response is OK
            if (!res.ok) {
              const errorText = await res.text();
              console.error('PayPal create order error:', {
                status: res.status,
                statusText: res.statusText,
                response: errorText.substring(0, 200)
              });
              throw new Error(`Failed to create PayPal order: ${res.status} ${res.statusText}`);
            }

            // Check content type
            const contentType = res.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
              const errorText = await res.text();
              console.error('PayPal create order returned non-JSON response:', {
                contentType,
                response: errorText.substring(0, 200)
              });
              throw new Error('Server returned invalid response format. Please check your PayPal API endpoint.');
            }

            return res.json();
          })
          .then(res => {
            if (!res || !res.order_id) {
              console.error('Invalid response from create order:', res);
              throw new Error('Invalid response from PayPal order creation');
            }
            return res.order_id;
          })
          .catch(error => {
            console.error('Error creating PayPal order:', error);
            notification.error("Failed to create payment order. Please try again or contact support.");
            throw error;
          });
        },

        /** CAPTURE ORDER (BACKEND) */
        onApprove: (data) => {
          // Construct the API URL - try with base URL first, fallback to relative
          const apiUrl = `${BACKEND_API_BASE_URL}paypal/capture-order`;
          
          return fetch(apiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              order_id: data.orderID
            })
          })
          .then(async (res) => {
            // Check if response is OK
            if (!res.ok) {
              const errorText = await res.text();
              console.error('PayPal capture order error:', {
                status: res.status,
                statusText: res.statusText,
                response: errorText.substring(0, 200)
              });
              throw new Error(`Failed to capture PayPal order: ${res.status} ${res.statusText}`);
            }

            // Check content type
            const contentType = res.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
              const errorText = await res.text();
              console.error('PayPal capture order returned non-JSON response:', {
                contentType,
                response: errorText.substring(0, 200)
              });
              throw new Error('Server returned invalid response format. Please check your PayPal API endpoint.');
            }

            return res.json();
          })
          .then(details => {
            if (!details || !details.order_id) {
              console.error('Invalid response from capture order:', details);
              throw new Error('Invalid response from PayPal order capture');
            }
            notification.success("Payment completed successfully!");
            onConfirmTempBooking({
              paymentMethod: 'paypal',
              paypalOrderId: details.order_id,
              paypalCaptureId: details.capture_id,
              totalPrice: totalAmount,
              couponCode
            });
          })
          .catch(error => {
            console.error('Error capturing PayPal order:', error);
            notification.error("Failed to process payment. Please try again or contact support.");
            this.setState({ paypalPaymentFailed: true, isInitializingPayPal: false });
            throw error;
          });
        },
        onError: (err) => {
          // Check if it's a zoid error
          const errorMessage = err?.message || err?.toString() || '';
          if (errorMessage.includes('zoid') || errorMessage.includes('destroyed')) {
            console.warn('PayPal zoid error detected, will retry initialization');
            // Don't show error to user, just retry
            setTimeout(() => {
              this.setState({ isInitializingPayPal: false });
              this.initializePayPalButtons();
            }, 500);
            return;
          }
          notification.error("Payment failed. Please try again.");
          console.error('PayPal payment error:', err);
          // Show PayPal payment failed component
          this.setState({ paypalPaymentFailed: true, isInitializingPayPal: false });
        },
        onCancel: (data) => {
          this.setState({ paypalPaymentFailed: true, isInitializingPayPal: false });
          notification.warning("You have cancelled the Payment.");
        }
      });

      this.paypalButtons = buttons;
      
      // Render buttons with error handling
      try {
        buttons.render(container);
      } catch (renderError) {
        console.error('Error rendering PayPal buttons:', renderError);
        // If render fails due to zoid error, retry after cleanup
        if (renderError.message && renderError.message.includes('zoid')) {
          this.setState({ isInitializingPayPal: false });
          setTimeout(() => {
            this.initializePayPalButtons();
          }, 500);
          return;
        }
        throw renderError;
      }

      // Reset initialization attempts on success
      this.setState({ 
        paypalInitializationAttempts: 0,
        isInitializingPayPal: false 
      });

      // Hide unwanted buttons after a short delay
      setTimeout(() => {
        const unwantedButtons = container.querySelectorAll(
          'button[data-funding-source="paylater"], ' +
          'button[data-funding-source="credit"], ' +
          'button[data-funding-source="venmo"], ' +
          'button[data-funding-source="applepay"], ' +
          'button[data-funding-source="googlepay"], ' +
          '[data-funding-source="paylater"], ' +
          '[data-funding-source="credit"], ' +
          '[data-funding-source="venmo"], ' +
          '[data-funding-source="applepay"], ' +
          '[data-funding-source="googlepay"]'
        );
        unwantedButtons.forEach(button => {
          button.style.display = 'none';
        });
      }, 200);
    } catch (error) {
      console.error('Error initializing PayPal buttons:', error);
      this.setState({ isInitializingPayPal: false });
      
      // If it's a zoid error, retry after a delay
      const errorMessage = error?.message || error?.toString() || '';
      if (errorMessage.includes('zoid') || errorMessage.includes('destroyed')) {
        console.log('Retrying PayPal initialization after zoid error...');
        setTimeout(() => {
          this.initializePayPalButtons();
        }, 1000);
      } else {
        notification.error("Failed to initialize PayPal. Please try again.");
      }
    }

    
    /*
    // Wait a bit before initializing to ensure cleanup is complete
    setTimeout(() => {
      if (window.paypal && !this.paypalButtons) {
        try {
          const buttons = window.paypal.Buttons({
            style: {
              layout: 'vertical',
              color: 'blue',
              shape: 'rect',
              label: 'paypal',
              height: 45
            },
            fundingSource: 'paypal',
            createOrder: (data, actions) => {
              return actions.order.create({
                purchase_units: [{
                  amount: {
                    value: totalAmount.toFixed(2),
                    currency_code: 'USD'
                  },
                  description: `Booking for ${this.props.event.event_name}`
                }]
              });
            },
            onApprove: (data, actions) => {
              return actions.order.capture().then((details) => {
                // Payment successful
                notification.success("Payment completed successfully!");
                
                // Update state to show payment completed
                this.setState({ paypalPaymentCompleted: true });
                
                // Call the booking function with PayPal payment details
                onConfirmTempBooking({
                  paymentMethod: 'paypal',
                  paypalOrderId: details.id,
                  paypalDetails: details,
                  totalPrice: totalAmount,
                  couponCode: couponCode,
                });
              });
            },
            onError: (err) => {
              notification.error("Payment failed. Please try again.");
              console.error('PayPal payment error:', err);
              // Show PayPal payment failed component
              this.setState({ paypalPaymentFailed: true });
            },
            onCancel: (data) => {
              this.setState({ paypalPaymentFailed: true });
              notification.warning("You have cancelled the Payment.");
            }
          });

          // Store the buttons instance for cleanup
          this.paypalButtons = buttons;
          
          // Render the buttons
          buttons.render(container);

          // Reset initialization attempts on success
          this.setState({ paypalInitializationAttempts: 0 });

          // Hide unwanted buttons after a short delay
          setTimeout(() => {
            const unwantedButtons = container.querySelectorAll(
              'button[data-funding-source="paylater"], ' +
              'button[data-funding-source="credit"], ' +
              'button[data-funding-source="venmo"], ' +
              'button[data-funding-source="applepay"], ' +
              'button[data-funding-source="googlepay"], ' +
              '[data-funding-source="paylater"], ' +
              '[data-funding-source="credit"], ' +
              '[data-funding-source="venmo"], ' +
              '[data-funding-source="applepay"], ' +
              '[data-funding-source="googlepay"]'
            );
            unwantedButtons.forEach(button => {
              button.style.display = 'none';
            });
          }, 200);
        } catch (error) {
          console.error('Error initializing PayPal buttons:', error);
          
          // Increment retry attempts
          const attempts = this.state.paypalInitializationAttempts + 1;
          this.setState({ paypalInitializationAttempts: attempts });
          
          // Retry up to 3 times
          if (attempts < 3) {
            console.log(`Retrying PayPal initialization (attempt ${attempts + 1}/3)`);
            setTimeout(() => {
              this.initializePayPalButtons();
            }, 1000 * attempts); // Exponential backoff
          } else {
            notification.error("Failed to initialize PayPal after multiple attempts. Please refresh the page.");
          }
        }
      }
    }, 100);
    */
  };

  /**
   * Clear PayPal buttons safely
   * Returns a promise that resolves when cleanup is complete
   */
  clearPayPalButtons = () => {
    return new Promise((resolve) => {
      const container = this.paypalContainerRef.current;
      
      // Clear the buttons instance first
      if (this.paypalButtons) {
        try {
          // Check if the buttons instance has a close method
          if (typeof this.paypalButtons.close === 'function') {
            this.paypalButtons.close();
          }
          // Check if the buttons instance has a destroy method
          if (typeof this.paypalButtons.destroy === 'function') {
            this.paypalButtons.destroy();
          }
        } catch (error) {
          console.warn('Error closing PayPal buttons:', error);
          // Continue with cleanup even if there's an error
        }
        this.paypalButtons = null;
      }
      
      // Clear the container after a delay to ensure buttons are properly closed
      // Increased delay to give PayPal SDK time to clean up zoid components
      if (container) {
        setTimeout(() => {
          // Remove all child elements
          while (container.firstChild) {
            container.removeChild(container.firstChild);
          }
          // Additional delay to ensure zoid cleanup is complete
          setTimeout(() => {
            resolve();
          }, 100);
        }, 200);
      } else {
        // If no container, resolve immediately
        setTimeout(() => resolve(), 100);
      }
    });
  };

  /**
   * Handle PayPal payment
   */
  onPayPalPayment = () => {
    if (!window.paypal) {
      this.loadPayPalSDK();
    } else {
      this.initializePayPalButtons();
    }
  };

  /**
   * Load Stripe SDK
   */
  loadStripeSDK = () => {
    const { paymentSettings } = this.props;
    
    console.log('Loading Stripe SDK...', {
      paymentSettings: paymentSettings,
      isStripeEnabled: paymentSettings?.is_stripe_enabled,
      hasPublishableKey: !!paymentSettings?.stripe_publishable_key
    });
    
    // Check if Stripe is enabled and publishable key exists
    if (!paymentSettings || 
        paymentSettings.is_stripe_enabled !== "E" || 
        !paymentSettings.stripe_publishable_key) {
      console.log('Stripe is not enabled or publishable key is missing');
      return;
    }

    // Check if Stripe is already loaded
    if (window.Stripe) {
      console.log('Stripe is already loaded');
      this.setState({ stripeLoaded: true });
      // Only initialize if card is selected and element doesn't exist
      if (this.state.selectedPaymentMethod === "card" && 
          !this.state.stripeCardElement && 
          !this.state.isInitializingStripe) {
        setTimeout(() => {
          this.initializeStripeElements();
        }, 100);
      }
      return;
    }

    // Check if script is already being loaded
    if (document.querySelector('script[src*="js.stripe.com"]')) {
      console.log('Stripe script is already being loaded');
      return;
    }
    
    console.log('Creating and loading Stripe script...');
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.async = true;
    script.onload = () => {
      console.log('Stripe SDK loaded successfully');
      this.setState({ stripeLoaded: true });
      // Only initialize if card is selected and element doesn't exist
      if (this.state.selectedPaymentMethod === "card" && 
          !this.state.stripeCardElement && 
          !this.state.isInitializingStripe) {
        setTimeout(() => {
          this.initializeStripeElements();
        }, 100);
      }
    };
    script.onerror = (error) => {
      console.error('Stripe SDK failed to load:', error);
      notification.error("Failed to load Stripe. Please check your internet connection and refresh the page.");
    };
    document.head.appendChild(script);
  };

  /**
   * Initialize Stripe Elements
   */
  initializeStripeElements = () => {
    console.log('############# initilize ###################### ',this.state.isInitializingStripe);
    // Prevent multiple simultaneous initializations
    if (this.state.isInitializingStripe) {
      console.log('Stripe initialization already in progress, skipping...');
      return;
    }

    // Only initialize if card is the selected payment method
    if (this.state.selectedPaymentMethod !== "card") {
      console.log('Card is not the selected payment method, skipping Stripe initialization');
      return;
    }

    const { paymentSettings } = this.props;
    
    console.log('Initializing Stripe Elements...', {
      hasStripe: !!window.Stripe,
      hasPublishableKey: !!paymentSettings?.stripe_publishable_key,
      publishableKey: paymentSettings?.stripe_publishable_key ? 'Present' : 'Missing',
      paymentSettings: paymentSettings
    });
    
    if (!window.Stripe) {
      console.error('Stripe is not loaded');
      notification.error("Stripe is not loaded. Please refresh the page.");
      return;
    }
    
    if (!paymentSettings?.stripe_publishable_key) {
      console.error('Stripe publishable key is missing');
      notification.error("Stripe configuration is missing. Please contact support.");
      return;
    }

    // Check if element already exists in state
    if (this.state.stripeCardElement) {
      console.log('Stripe Card Element already exists in state, skipping initialization');
      return;
    }

    // Set flag to prevent multiple initializations
    this.setState({ isInitializingStripe: true });

    try {
      // Clean up any existing elements first to prevent "Can only create one Element" error
      if (this.state.stripeCardElement) {
        try {
          this.state.stripeCardElement.unmount();
        } catch (error) {
          console.warn('Error unmounting existing card element:', error);
        }
      }

      // Destroy existing Elements instance if it exists (this prevents the error)
      if (this.elements) {
        console.log('Cleaning up existing Stripe Elements instance');
        // Elements instances don't have a destroy method, but we can null it
        // and create a new one to ensure clean state
        this.elements = null;
      }

      // Initialize Stripe instance
      if (!this.stripe) {
        console.log('Creating Stripe instance with key:', paymentSettings.stripe_publishable_key.substring(0, 10) + '...');
        this.stripe = window.Stripe(paymentSettings.stripe_publishable_key, {
          stripeAccount: paymentSettings.admin_stripe_account_id
        });
      }

      // Create new Elements instance (always create fresh to avoid conflicts)
      console.log('Creating new Stripe Elements instance');
      this.elements = this.stripe.elements();

      const elementStyle = {
        base: {
          fontSize: '16px',
          color: '#1a365d',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          '::placeholder': {
            color: '#a0aec0',
          },
          padding: '16px 20px',
          lineHeight: '1.5',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
        },
        invalid: {
          color: '#e53e3e',
          iconColor: '#e53e3e',
        },
        complete: {
          color: '#38a169',
          iconColor: '#38a169',
        },
      };

      console.log('Creating Stripe Card Element...');

      // Create single card element with Calendly-style appearance
      const cardElement = this.elements.create('card', {
        style: elementStyle,
        hidePostalCode: true,
        classes: {
          base: 'stripe-card-element',
          complete: 'stripe-card-element-complete',
          empty: 'stripe-card-element-empty',
          focus: 'stripe-card-element-focus',
          invalid: 'stripe-card-element-invalid',
          webkitAutofill: 'stripe-card-element-webkit-autofill',
        },
      });

      console.log('Stripe Card Element created successfully');

      // Mount card element
      const cardContainer = this.stripeCardElementRef.current;
      if (cardContainer) {
        console.log('Mounting card element');
        cardElement.mount(cardContainer);
        
        cardElement.on('change', (event) => {
          this.setState({
            stripeCardErrors: event.error ? event.error.message : null,
          });
        });

        this.setState({ 
          stripeCardElement: cardElement,
          isInitializingStripe: false 
        });
        console.log('Stripe Card Element initialized successfully');
      } else {
        console.error('Card container not found');
        this.setState({ isInitializingStripe: false });
      }
    } catch (error) {
      console.error('Error initializing Stripe Elements:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      
      // Check if it's the "Can only create one Element" error
      if (error.message && error.message.includes('Can only create one Element')) {
        console.warn('Stripe element already exists, cleaning up and retrying...');
        // Clean up and retry after a delay
        this.cleanupStripeElements();
        this.setState({ isInitializingStripe: false });
        setTimeout(() => {
          this.initializeStripeElements();
        }, 500);
        return;
      }
      
      notification.error("Failed to initialize Stripe payment form. Please refresh the page and try again.");
      this.setState({ isInitializingStripe: false });
    }
  };




  /**
   * Clean up Stripe Elements
   */
  cleanupStripeElements = () => {
    // Unmount card element
    if (this.state.stripeCardElement) {
      try {
        this.state.stripeCardElement.unmount();
      } catch (error) {
        console.warn('Error unmounting Stripe card element:', error);
      }
    }

    // Clear container
    const cardContainer = this.stripeCardElementRef.current;
    if (cardContainer) {
      cardContainer.innerHTML = '';
    }

    // Destroy Elements instance to allow creating new one
    // This prevents "Can only create one Element" error
    this.elements = null;

    // Only update state if values actually changed to prevent infinite loops
    if (this.state.stripeCardElement !== null || 
        this.state.stripeCardErrors !== null || 
        this.state.isInitializingStripe !== false) {
      this.setState({ 
        stripeCardElement: null,
        stripeCardErrors: null,
        isInitializingStripe: false
      });
    }
  };

  /**
   * Process Stripe payment
   */
  processStripePayment = async () => {
    const { onConfirmTempBooking } = this.props;
    const { stripeCardElement, couponCode, discount } = this.state;

    if (!stripeCardElement || !this.stripe) {
      notification.error("Stripe is not properly initialized.");
      return;
    }

    this.setState({ isProcessingStripePayment: true });

    try {
      // Create payment method with Stripe using card element
      const { error, paymentMethod } = await this.stripe.createPaymentMethod({
        type: 'card',
        card: stripeCardElement,
        billing_details: {
          // You can add billing details here if needed
        },
      });

      if (error) {
        notification.error(error.message || "Payment failed. Please try again.");
        this.setState({ isProcessingStripePayment: false });
        return;
      }

      // Calculate total amount
      const totalPricing = this.getTotalPricing();
      const totalAddOns = this.getTotalAddons();
      const totalAmount = totalPricing + totalAddOns - discount;

      // Send payment method ID to server
      onConfirmTempBooking({
        paymentMethod: 'card',
        stripePaymentMethodId: paymentMethod.id,
        paypalOrderId: null,
        paypalDetails: null,
        totalPrice: totalAmount,
        couponCode: couponCode,
      });

      this.setState({ isProcessingStripePayment: false });
    } catch (error) {
      console.error('Stripe payment error:', error);
      notification.error("An unexpected error occurred during payment.");
      this.setState({ isProcessingStripePayment: false });
    }
  };

  /**
   * Get available payment methods based on settings
   */
  getAvailablePaymentMethods = () => {
    const { paymentSettings } = this.props;
    const methods = [];

    if (paymentSettings) {
      if (paymentSettings.is_paypal_enabled === "E") {
        methods.push({ id: "paypal", name: "PayPal", icon: PaypalSVG });
      }
      if (paymentSettings.is_stripe_enabled === "E") {
        methods.push({ id: "card", name: "Credit or Debit Card", icon: CreditCardSVG });
      }
    }

    return methods;
  };

  /**
   * Component did mount
   */
  componentDidMount() {
    const availableMethods = this.getAvailablePaymentMethods();
    
    if (availableMethods.length > 0) {
      // Set the first available method as default
      this.setState({ selectedPaymentMethod: availableMethods[0].id });
    }

    // Load PayPal SDK if PayPal is available
    if (availableMethods.some(method => method.id === "paypal")) {
      this.loadPayPalSDK();
    }

    // Load Stripe SDK if Stripe is available
    if (availableMethods.some(method => method.id === "card")) {
      this.loadStripeSDK();
    }
  }

  /**
   * Component did update
   */
  componentDidUpdate(prevProps, prevState) {
    // Prevent infinite loops - only process if payment method actually changed
    const paymentMethodChanged = prevState.selectedPaymentMethod !== this.state.selectedPaymentMethod;
    const stripeLoadedChanged = prevState.stripeLoaded !== this.state.stripeLoaded;
    
    // Skip if we're already processing an update to prevent infinite loops
    if (this.isProcessingPayPalUpdate || this.isProcessingStripeUpdate) {
      return;
    }

    // Clear PayPal buttons when switching away from PayPal
    if (paymentMethodChanged &&
        prevState.selectedPaymentMethod === "paypal" && 
        this.state.selectedPaymentMethod !== "paypal") {
      this.isProcessingPayPalUpdate = true;
      // Clear buttons when switching away
      this.clearPayPalButtons().finally(() => {
        this.isProcessingPayPalUpdate = false;
      }).catch(err => {
        console.warn('Error clearing PayPal buttons:', err);
        this.isProcessingPayPalUpdate = false;
      });
      return; // Exit early to prevent other logic from running
    }
    
    // Re-initialize PayPal buttons when payment method changes to PayPal
    if (paymentMethodChanged &&
        this.state.selectedPaymentMethod === "paypal" && 
        prevState.selectedPaymentMethod !== "paypal") {
      this.isProcessingPayPalUpdate = true;
      // First clear any existing buttons, then initialize after delay
      this.clearPayPalButtons().then(() => {
        setTimeout(() => {
          this.initializePayPalButtons().finally(() => {
            this.isProcessingPayPalUpdate = false;
          });
        }, 400);
      }).catch(err => {
        console.warn('Error during PayPal cleanup before initialization:', err);
        // Still try to initialize after delay
        setTimeout(() => {
          this.initializePayPalButtons().finally(() => {
            this.isProcessingPayPalUpdate = false;
          });
        }, 400);
      });
      return; // Exit early to prevent other logic from running
    }

    // Clean up Stripe Elements when switching away from card payment
    if (paymentMethodChanged &&
        prevState.selectedPaymentMethod === "card" && 
        this.state.selectedPaymentMethod !== "card") {
      this.isProcessingStripeUpdate = true;
      this.cleanupStripeElements();
      // Use requestAnimationFrame to reset flag after state update completes
      requestAnimationFrame(() => {
        this.isProcessingStripeUpdate = false;
      });
      return; // Exit early to prevent other logic from running
    }

    // Initialize Stripe Elements when switching to card payment OR when Stripe SDK loads
    // Only initialize if not already initializing and element doesn't exist
    if (this.state.selectedPaymentMethod === "card" && 
        this.state.stripeLoaded && 
        !this.state.stripeCardElement &&
        !this.state.isInitializingStripe &&
        (paymentMethodChanged || stripeLoadedChanged)) {
      console.log('Attempting to initialize Stripe Elements in componentDidUpdate');
      this.isProcessingStripeUpdate = true;
      // Don't cleanup here - it will cause a loop. Only cleanup if switching FROM another method
      if (paymentMethodChanged && prevState.selectedPaymentMethod && prevState.selectedPaymentMethod !== "card") {
        // Cleanup was already done above when switching away from the previous method
        // Just proceed with initialization
      }
      setTimeout(() => {
        this.initializeStripeElements();
        // Reset flag after initialization (which sets state)
        setTimeout(() => {
          this.isProcessingStripeUpdate = false;
        }, 100);
      }, 300);
    }
  }

  /**
   * Component will unmount
   */
  componentWillUnmount() {
    // Clean up PayPal buttons safely
    // Note: We can't await in componentWillUnmount, but we try to clean up
    this.clearPayPalButtons().catch(err => {
      console.warn('Error cleaning up PayPal buttons on unmount:', err);
    });
    // Clean up Stripe Elements
    this.cleanupStripeElements();
  }


  render() {
    const _this = this;
    const { event, onScheduleEvent, addOns, pricing, specialRibbon, onConfirmTempBooking } =
      this.props;

    const { isLoading, discount, paypalPaymentFailed, selectedPaymentMethod, stripeCardErrors, isProcessingStripePayment } = this.state;

    // Show PayPal payment failed component if payment failed
    if (paypalPaymentFailed) {
      return (
        <PaypalPaymentFailed 
          onRetryPayment={this.onRetryPayPalPayment}
          event={event}
          bookingData={{
            firstName: this.props.firstName,
            lastName: this.props.lastName,
            email: this.props.email,
            messageFromCustomer: this.props.messageFromCustomer,
            bookingDate: this.props.bookingDate,
            selectedSlot: this.props.selectedSlot,
            timezoneValue: this.props.timezoneValue,
            bookingId: this.props.bookingId
          }}
        />
      );
    }

    let totalPricing = this.getTotalPricing();
    let totalAddOns = this.getTotalAddons();

    // Get available payment methods
    const availablePaymentMethods = this.getAvailablePaymentMethods();

    // If no payment methods are available, show error
    if (availablePaymentMethods.length === 0) {
      return (
        <div className="alert alert-warning text-center">
          <h4>Payment Not Available</h4>
          <p>No payment methods are configured for this event. Please contact the event organizer.</p>
        </div>
      );
    }

    return (
      <>
        <Loader isShowLoader={isLoading} />
        <div className="row">
          {!isEmpty(specialRibbon) && (
            <div className="col-12">
              <nav className="navbar navbar-expand-sm main_noti shadow">
                <ul className="navbar-nav">
                  <li className="nav-item noti_side">
                    <img src={One} />
                  </li>

                  <li className="nav-item left_noti ml-3 ml-md-0">
                    <span>{specialRibbon}</span>
                  </li>
                </ul>
              </nav>
            </div>
          )}
          <div className="col-12">
            <div className="booking_arrow row align-items-center mx-0">
              <div className="col-auto">
                <img src={ArrowLeft} alt="Arrow Left" />
              </div>
              <div className="col-auto ps-0">
                <p>Back</p>
              </div>
              <h1> {event.event_name} </h1>
            </div>
          </div>
          <div className="col-12">
            <div
              className="main_add p-3 border-0 booking_pay_full"
              style={{ borderRadius: 5 }}
            >
              <label className="form-group-label">
                This Event Accepts
              </label>
              <FormGroup check>
                <Input name="radio1" type="radio" checked />
                <Label htmlFor="radio1" check>
                  Full Payment
                </Label>
              </FormGroup>
            </div>
          </div>
        </div>

        <Formik
          validationSchema={noValidation}
          initialValues={initialValues}
          enableReinitialize
          onSubmit={(data) => {
            const { selectedPaymentMethod } = _this.state;
            
            if (selectedPaymentMethod === "paypal") {
              // For PayPal, we'll handle payment through PayPal buttons
              _this.onPayPalPayment();
            } else if (selectedPaymentMethod === "card") {
              // For Stripe card payment, process through Stripe
              _this.processStripePayment();
            }
          }}
        >
          {(formProps) => {
            const { values, errors, touched, handleChange, setFieldValue } =
              formProps;

            return (
              <Form>
                <div className="row sub_div justify-content-end">
                  <div className="col-lg-6 main_add booking_payment_cards">
                    <div className="head">
                      <h3>Payment Summary</h3>
                    </div>
                    <div className="body">
                      <div className="row justify-content-between">
                        <div className="col-lg-5 col-6">
                          <p>
                            Price Item <span>({pricing.length})</span>
                          </p>
                        </div>
                        <div className="col-auto price">
                          <p>${totalPricing}</p>
                        </div>
                      </div>
                      <div className="row justify-content-between">
                        <div className="col-lg-5 col-6">
                          <p>
                            Add On Item <span>({addOns.length})</span>
                          </p>
                        </div>
                        <div className="col-auto price">
                          <p>${totalAddOns}</p>
                        </div>
                      </div>
                      <div className="row justify-content-between">
                        <div className="col-lg-5 col-6">
                          <p>Full Payment</p>
                        </div>
                        <div className="col-auto price">
                          <p>${totalPricing + totalAddOns}</p>
                        </div>
                      </div>
                      <div className="row justify-content-between">
                        <div className="col-12">
                          <label className="form-group-label">
                            Have A Coupon Code
                          </label>
                          <div className="form-group coupon_code">
                            <input
                            
                              type="text"
                              className="form-control"
                              placeholder="Nf162"
                              onChange={(event) => {
                                this.setState({
                                  couponCode: event.target.value,
                                });
                              }}
                            />
                            <button
                              className="btn btn-app"
                              type="button"
                              onClick={this.onApplyCouponCode}
                            >
                              Apply
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="row justify-content-between">
                        <div className="col-lg-5 col-6">
                          <p>Discount</p>
                        </div>
                        <div className="col-auto price">
                          <p>$ {discount}</p>
                        </div>
                      </div>
                      <div className="row justify-content-between total">
                        <div className="col-lg-5 col-6">
                          <p>Total</p>
                        </div>
                        <div className="col-auto price">
                          <p>${totalPricing + totalAddOns - discount}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-6 main_add booking_payment_cards">
                    <div className="head">
                      <h3>Payment Method</h3>
                    </div>

                    <div className="body">
                      <div className="row">
                        {/* Dynamically render available payment methods */}
                        {availablePaymentMethods.map((method, index) => (
                          <div key={method.id} className="col-12 mb-3">
                            <div 
                              className="payment-option d-flex align-items-center p-4 border rounded"
                              style={{
                                background: selectedPaymentMethod === method.id ? "#f8fafc" : "#fff",
                                borderColor: selectedPaymentMethod === method.id ? "#3182ce" : "#e2e8f0",
                                borderWidth: selectedPaymentMethod === method.id ? "2px" : "1px",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                borderRadius: "12px",
                                boxShadow: selectedPaymentMethod === method.id ? "0 2px 4px rgba(49, 130, 206, 0.1)" : "0 1px 2px rgba(0, 0, 0, 0.05)"
                              }}
                              onClick={() => _this.onPaymentMethodChange(method.id)}
                            >
                              <input
                                className="form-check-input me-3"
                                type="radio"
                                name="payment_option"
                                id={`${method.id}_option`}
                                value={method.id}
                                checked={selectedPaymentMethod === method.id}
                                onChange={() => _this.onPaymentMethodChange(method.id)}
                                style={{ accentColor: "#3182ce", marginLeft: '0' }}
                              />
                              <label
                                className="form-check-label d-flex align-items-center"
                                htmlFor={`${method.id}_option`}
                                style={{ cursor: "pointer", marginBottom: 0 }}
                              >
                                <img src={method.icon} alt={method.name} style={{ height: 36, width: 'auto' }} className="me-3" />
                                <span style={{ fontWeight: 600, fontSize: 16, color: '#1a365d' }}>{method.name}</span>
                              </label>
                            </div>
                          </div>
                        ))}

                        {selectedPaymentMethod === "card" && (
                          <div className="col-12">
                            <div style={{ marginBottom: '24px' }}>
                              <label style={{ 
                                display: 'block', 
                                color: '#1a365d', 
                                fontSize: '14px', 
                                fontWeight: '600', 
                                marginBottom: '16px' 
                              }}>
                                Card Information
                              </label>
                              
                              {/* Single Card Element with Calendly-style appearance */}
                              <div 
                                ref={_this.stripeCardElementRef}
                                id="stripe-card-element"
                                className="stripe-card-element-container"
                                style={{
                                  width: '100%',
                                  minHeight: '56px',
                                  border: stripeCardErrors ? '2px solid #e53e3e' : '1px solid #e2e8f0',
                                  borderRadius: '12px',
                                  backgroundColor: '#fff',
                                  padding: '0',
                                  boxSizing: 'border-box',
                                  display: 'flex',
                                  alignItems: 'stretch',
                                  transition: 'all 0.2s ease',
                                  fontSize: '16px',
                                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                  position: 'relative',
                                  overflow: 'hidden'
                                }}
                              ></div>
                              
                              {stripeCardErrors && (
                                <div style={{ 
                                  color: '#e53e3e', 
                                  fontSize: '13px', 
                                  marginTop: '8px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}>
                                  <i className="fas fa-exclamation-circle" style={{ fontSize: '12px' }}></i>
                                  {stripeCardErrors}
                                </div>
                              )}
                            </div>

                            {/* Loading State */}
                            {!_this.state.stripeCardElement && _this.state.stripeLoaded && (
                              <div style={{ marginBottom: '20px' }}>
                                <div style={{ 
                                  color: '#3182ce', 
                                  fontSize: '14px', 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '10px',
                                  padding: '12px 16px',
                                  backgroundColor: '#f7fafc',
                                  borderRadius: '8px',
                                  border: '1px solid #e2e8f0'
                                }}>
                                  <i className="fas fa-spinner fa-spin"></i>
                                  Loading secure payment form...
                                </div>
                                <div style={{ 
                                  marginTop: '12px',
                                  textAlign: 'center'
                                }}>
                                  <button 
                                    type="button"
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={() => {
                                      console.log('Manual retry of Stripe initialization');
                                      _this.initializeStripeElements();
                                    }}
                                    style={{
                                      fontSize: '12px',
                                      padding: '6px 12px'
                                    }}
                                  >
                                    <i className="fas fa-redo me-1"></i>
                                    Retry Loading
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* Error State */}
                            {_this.state.stripeLoaded && !_this.state.stripeCardElement && (
                              <div style={{ marginBottom: '20px' }}>
                                <div style={{ 
                                  color: '#e53e3e', 
                                  fontSize: '14px', 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '10px',
                                  padding: '12px 16px',
                                  backgroundColor: '#fed7d7',
                                  borderRadius: '8px',
                                  border: '1px solid #feb2b2'
                                }}>
                                  <i className="fas fa-exclamation-triangle"></i>
                                  Failed to load payment form. Please try again.
                                </div>
                                <div style={{ 
                                  marginTop: '12px',
                                  textAlign: 'center'
                                }}>
                                  <button 
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={() => {
                                      console.log('Manual retry of Stripe initialization');
                                      _this.initializeStripeElements();
                                    }}
                                    style={{
                                      fontSize: '12px',
                                      padding: '6px 12px'
                                    }}
                                  >
                                    <i className="fas fa-redo me-1"></i>
                                    Retry
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* Security Notice */}
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '8px',
                              padding: '12px 16px',
                              backgroundColor: '#f0fff4',
                              borderRadius: '8px',
                              border: '1px solid #c6f6d5',
                              marginTop: '16px'
                            }}>
                              <i className="fas fa-shield-alt" style={{ color: '#38a169', fontSize: '14px' }}></i>
                              <small style={{ color: '#2f855a', fontSize: '13px', fontWeight: '500' }}>
                                Your payment information is secure and encrypted
                              </small>
                              <img 
                                src={StripeSVG} 
                                alt="Stripe" 
                                style={{ 
                                  height: '16px', 
                                  width: 'auto', 
                                  marginLeft: 'auto',
                                  opacity: 0.8
                                }} 
                              />
                            </div>
                          </div>
                        )}

                        {selectedPaymentMethod === "paypal" && (
                          <div className="col-12">
                            <div className="paypal-payment-container">
                              {_this.state.paypalPaymentCompleted ? (
                                <div className="alert alert-success text-center">
                                  <i className="fas fa-check-circle me-2"></i>
                                  PayPal Payment Completed Successfully!
                                </div>
                              ) : (
                                <div ref={_this.paypalContainerRef} className="paypal-buttons-only"></div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 text-end mt-4">
                    {selectedPaymentMethod === "card" && (
                      <button 
                        type="submit" 
                        className="btn btn-app"
                        disabled={isProcessingStripePayment || !_this.state.stripeCardElement}
                      >
                        {isProcessingStripePayment ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Processing...
                          </>
                        ) : (
                          'Confirm Booking'
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </>
    );
  }
}

export default connect(null, {
  getPromoByCode,
})(BookingStep3);
